import React, { useState, useEffect } from 'react';
import './AdminNotifications.scss';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newCustomerActivity, setNewCustomerActivity] = useState([]);
  const [oldCustomerActivity, setOldCustomerActivity] = useState([]);

  useEffect(() => {
    fetchRecentActivity();
    // Refresh every 30 seconds
    const interval = setInterval(fetchRecentActivity, 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchRecentActivity = async () => {
    try {
      const [customers, subscriptions, orders, expiringSubscriptions] = await Promise.all([
        fetchRecentCustomers(),
        fetchRecentSubscriptions(),
        fetchRecentOrders(),
        fetchExpiringSubscriptions()
      ]);

      // Filter customers by selected date
      const filteredCustomers = customers.filter(c => {
        const itemDate = new Date(c.createdAt).toISOString().split('T')[0];
        return itemDate === selectedDate;
      });

      // Get new customer IDs from selected date
      const newCustomerIds = filteredCustomers.map(c => c._id);

      // Filter subscriptions by date
      const dateSubscriptions = subscriptions.filter(s => {
        const subDate = new Date(s.startDate).toISOString().split('T')[0];
        return subDate === selectedDate;
      });

      // Filter orders by date
      const dateOrders = orders.filter(o => {
        const orderDate = new Date(o.date).toISOString().split('T')[0];
        return orderDate === selectedDate;
      });

      // Separate new customer activity
      const newCustomerSubs = dateSubscriptions.filter(s => newCustomerIds.includes(s.customerId));
      const newCustomerOrders = dateOrders.filter(o => newCustomerIds.includes(o.customerId));

      // Separate old customer activity
      const oldCustomerSubs = dateSubscriptions.filter(s => !newCustomerIds.includes(s.customerId));
      const oldCustomerOrders = dateOrders.filter(o => !newCustomerIds.includes(o.customerId));

      const newActivity = [
        ...filteredCustomers.map(c => ({ type: 'customer', data: c, timestamp: c.createdAt })),
        ...newCustomerSubs.map(s => ({ type: 'subscription', data: s, timestamp: s.startDate })),
        ...newCustomerOrders.map(o => ({ type: 'order', data: o, timestamp: o.date }))
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const oldActivity = [
        ...oldCustomerSubs.map(s => ({ type: 'subscription', data: s, timestamp: s.startDate })),
        ...oldCustomerOrders.map(o => ({ type: 'order', data: o, timestamp: o.date }))
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const allNotifications = [
        ...newActivity,
        ...oldActivity,
        ...expiringSubscriptions.map(s => ({ type: 'expiring', data: s, timestamp: s.nextBillingDate }))
      ];

      setNewCustomerActivity(newActivity);
      setOldCustomerActivity(oldActivity);
      setNotifications(allNotifications);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const fetchRecentCustomers = async () => {
    try {
      const { directDB } = await import('../../db/direct-couchdb');
      const result = await directDB.find({
        selector: { type: 'customer' },
        sort: [{ createdAt: 'desc' }],
        limit: 100
      });
      return result.docs;
    } catch (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
  };

  const fetchRecentSubscriptions = async () => {
    try {
      const subscriptionService = await import('../../services/subscription-service');
      const allSubs = await subscriptionService.default.getAllSubscriptions();
      return allSubs.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      return [];
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const orderService = await import('../../db/order-service');
      const allOrders = await orderService.default.getAllOrders();
      return allOrders.sort((a, b) => new Date(b.date) - new Date(a.date));
    } catch (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
  };

  const fetchExpiringSubscriptions = async () => {
    try {
      const subscriptionService = await import('../../services/subscription-service');
      const allSubs = await subscriptionService.default.getAllSubscriptions();
      
      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000));
      
      return allSubs.filter(sub => {
        if (sub.status !== 'active') return false;
        const nextBilling = new Date(sub.nextBillingDate);
        return nextBilling <= threeDaysFromNow && nextBilling > now;
      });
    } catch (error) {
      console.error('Error fetching expiring subscriptions:', error);
      return [];
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'customer': return '👤';
      case 'subscription': return '📅';
      case 'order': return '🛒';
      case 'expiring': return '⚠️';
      default: return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'customer': return '#2196F3';
      case 'subscription': return '#4CAF50';
      case 'order': return '#FF9800';
      case 'expiring': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="admin-notifications loading">Loading notifications...</div>;
  }

  return (
    <div className="admin-notifications">
      <div className="notifications-header">
        <h2>🔔 Activity Notifications</h2>
        <div className="header-controls">
          <input 
            type="date" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          <button onClick={fetchRecentActivity} className="refresh-btn">🔄 Refresh</button>
        </div>
      </div>

      <div className="notifications-container">
        <div className="notifications-column">
          <h3>Existing Customers Activity</h3>
          <div className="notifications-list">
            {oldCustomerActivity.length === 0 ? (
              <div className="no-notifications">No activity from existing customers</div>
            ) : (
              oldCustomerActivity.map((notification, index) => (
                <div 
                  key={index} 
                  className="notification-item"
                  style={{ borderLeftColor: getNotificationColor(notification.type) }}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    {notification.type === 'subscription' && (
                      <>
                        <div className="notification-title">New Subscription</div>
                        <div className="notification-details">
                          <strong>{notification.data.planType}</strong> plan
                          <span className="notification-amount">₹{notification.data.monthlyPrice}/month</span>
                        </div>
                      </>
                    )}
                    {notification.type === 'order' && (
                      <>
                        <div className="notification-title">New Order</div>
                        <div className="notification-details">
                          <strong>{notification.data.customerName || 'Customer'}</strong>
                          <span className="notification-amount">₹{notification.data.totalAmount}</span>
                          <span className="notification-items">{notification.data.items?.length || 0} items</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="notification-time">
                    {formatTimestamp(notification.timestamp)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="notifications-column">
          <h3>New Customers Activity</h3>
          <div className="notifications-list">
            {newCustomerActivity.length === 0 ? (
              <div className="no-notifications">No new customers on {new Date(selectedDate).toLocaleDateString()}</div>
            ) : (
              newCustomerActivity.map((notification, index) => (
            <div 
              key={index} 
              className="notification-item"
              style={{ borderLeftColor: getNotificationColor(notification.type) }}
            >
              <div className="notification-icon">
                {getNotificationIcon(notification.type)}
              </div>
              <div className="notification-content">
                {notification.type === 'customer' && (
                  <>
                    <div className="notification-title">New Customer Registered</div>
                    <div className="notification-details">
                      <strong>{notification.data.name}</strong>
                      <span className="notification-email">{notification.data.email}</span>
                    </div>
                  </>
                )}
                {notification.type === 'subscription' && (
                  <>
                    <div className="notification-title">New Subscription</div>
                    <div className="notification-details">
                      <strong>{notification.data.planType}</strong> plan
                      <span className="notification-amount">₹{notification.data.monthlyPrice}/month</span>
                    </div>
                  </>
                )}
                {notification.type === 'order' && (
                  <>
                    <div className="notification-title">New Order</div>
                    <div className="notification-details">
                      <strong>{notification.data.customerName || 'Customer'}</strong>
                      <span className="notification-amount">₹{notification.data.totalAmount}</span>
                      <span className="notification-items">{notification.data.items?.length || 0} items</span>
                    </div>
                  </>
                )}
                {notification.type === 'expiring' && (
                  <>
                    <div className="notification-title" style={{ color: '#f44336' }}>⚠️ Subscription Expiring Soon</div>
                    <div className="notification-details">
                      <strong>{notification.data.planType}</strong> plan
                      <span className="notification-amount" style={{ color: '#f44336' }}>Expires: {new Date(notification.data.nextBillingDate).toLocaleDateString()}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="notification-time">
                {formatTimestamp(notification.timestamp)}
              </div>
            </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotifications;
